// @flow
import React from 'react';
import styled from 'styled-components';
import { Image } from '@finect/tabular-components/Image';
import { Container } from '@finect/tabular-core/Container';
import { ContainerInner } from '@finect/tabular-core/ContainerInner';
import { finectAPI } from '@finect/front-utils/finectAPI';
import { MainContent } from '@finect/tabular-core/MainContent';
import { Section } from '@finect/tabular-core/Section';
import { Small } from '@finect/tabular-core/Small';
import { AppLink } from '@finect/tabular-core/AppLink';
import { DatalayerScene } from '@finect/front-gtm';
import { Head } from '@finect/tabular-components/Head';
import { buildOpenGraph, buildTwitter } from '../../data/seo';

import {
  Disclaimer,
  Benefit,
  BenefitIMG,
  BenefitInfo,
  HowItWorks,
  MainProductsReviews,
  Step,
  SectionTitle,
  HeroLanding,
  NewSectionInner,
  TopQuestions,
  FaqItem,
  BottomCTA,
  ArticleCTA,
  HighlightsCompaniesContent,
  LogoContent,
  HighlightsCompanies,
  LandingContent,
  PartnersDisclaimer,
  Provinces
} from '../../components';

import objetivosIMG from '../../assets/objetivos.webp';
import tranquilidadIMG from '../../assets/tranquilidad.webp';
import tiempoIMG from '../../assets/tiempo.webp';
import {
  getBreadcrumbSchemaJSON,
  getFAQSchemaJSON,
  getItemListSchemaJSON,
  getServiceSchemaJSON
} from './utils/schema';

const ProfessionalHelp = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

type Props = {
  staticContext?: Object,
  toggleModal: Function,
  location: Object,
  history: Object
};

type State = {
  enterpriseAdvisers: Object,
  provinces: Array<Object>
};

export class Landing extends React.Component<Props, State> {
  state =
    typeof window === 'undefined'
      ? {
        enterpriseAdvisers: (this.props.staticContext && this.props.staticContext.initialData.landing) || [],
        provinces: (this.props.staticContext && this.props.staticContext.initialData.provinces) || []
      }
      : {
        enterpriseAdvisers: (window.initialData && window.initialData.landing) || [],
        provinces: (window.initialData && window.initialData.provinces) || []
      };

  componentDidMount() {
    if (!this.state.enterpriseAdvisers.length) {
      Landing.loadInitialData().then(enterpriseAdvisers =>
        this.setState({ enterpriseAdvisers: enterpriseAdvisers || [] }));
    }

    if (!this.state.provinces.length) {
      Landing.loadProvinces().then(provinces =>
        this.setState({ provinces: provinces || [] }));
    }
  }

  static loadInitialData = () =>
    finectAPI({
      version: 'v4',
      path: `contents/profiles/organizations?filter=features/enterprise+eq+true+and+tag+eq+34490&orderby=evergreen`,
      method: 'GET',
      onSuccess: ({ data }: { data: Array<Object> }) => data,
      onError: () => { }
    });

  static loadProvinces = () =>
    finectAPI({
      version: 'v4',
      path: 'bff/advisors/provinces',
      method: 'GET',
      onSuccess: (provinces: Object) =>
        provinces.data.map(province => ({
          ...province,
          province: province.province.split('/')[0]
        })),
      onError: () => []
    });

  render() {
    const { staticContext, toggleModal } = this.props;
    const { enterpriseAdvisers, provinces } = this.state;

    return (
      <LandingContent>
        <Head
          title="Asesor financiero: encuentra el mejor para ti - Finect"
          withoutName
          description="Encuentra tu asesor financiero de confianza que te ayude a planificar tus inversiones y finanzas para conseguir tus objetivos económicos."
          canonical="/asesores"
          robots="index, follow"
          openGraph={buildOpenGraph({
            title: 'Asesor financiero: encuentra el mejor para ti - Finect',
            description:
              'Encuentra tu asesor financiero de confianza que te ayude a planificar tus inversiones y finanzas para conseguir tus objetivos económicos.',
            url: '/asesores'
          })}
          twitter={buildTwitter({
            title: 'Asesor financiero: encuentra el mejor para ti - Finect',
            description:
              'Encuentra tu asesor financiero de confianza que te ayude a planificar tus inversiones y finanzas para conseguir tus objetivos económicos.'
          })}
        >
          <script type="application/ld+json">{getBreadcrumbSchemaJSON()}</script>
          <script type="application/ld+json">{getFAQSchemaJSON()}</script>
          {provinces && provinces.length > 0 && (
            <script type="application/ld+json">{getItemListSchemaJSON(provinces)}</script>
          )}
          {provinces && provinces.length > 0 && (
            <script type="application/ld+json">{getServiceSchemaJSON(provinces)}</script>
          )}
        </Head>
        <DatalayerScene pageType="home" pageId="advisers" />

        <HeroLanding toggleModal={toggleModal} />

        <Section>
          <HighlightsCompanies>
            <SectionTitle title="Nuestros partners" noMargin />
            <HighlightsCompaniesContent>
              {enterpriseAdvisers && enterpriseAdvisers.length > 0
                ? enterpriseAdvisers.map((enterprise, index) => (
                  <LogoContent key={index} qty={enterpriseAdvisers.length}>
                    <a href={`/grupos/${enterprise.alias}`} title={`Ver ${enterprise.displayName} en Finect`}>
                      <Image
                        src={`/image/${enterprise.logo}`}
                        height={180}
                        format="webp"
                        alt={enterprise.displayName}
                      />
                    </a>
                  </LogoContent>
                ))
                : null}
            </HighlightsCompaniesContent>

            <NewSectionInner textAlign="center">
              <PartnersDisclaimer note="No constituyen una recomendación personalizada." />
            </NewSectionInner>
          </HighlightsCompanies>
        </Section>

        <MainProductsReviews staticContext={staticContext} />

        <Benefit>
          <div>
            <BenefitIMG>
              <img src={objetivosIMG} alt="Alcanza tus objetivos" width="1000" height="667" loading="lazy" />
            </BenefitIMG>
          </div>
          <BenefitInfo>
            <h2>Alcanza tus objetivos</h2>
            <p>
              Tu <b>asesor financiero</b> se convertirá en el compañero de viaje que te ayudará a alcanzar tus
              sueños y los de tu familia. Conocerá tus objetivos, tus necesidades y el riesgo que puedes asumir.
              Un <b>asesor financiero</b> te ayudará a <b>planificar tus finanzas personales</b> para que
              nunca tengas que renunciar a tus metas.
            </p>
          </BenefitInfo>
        </Benefit>
        <Benefit reverse>
          <div>
            <BenefitIMG right>
              <img src={tranquilidadIMG} alt="Tranquilidad y rentabilidad" width="1000" height="667" loading="lazy" />
            </BenefitIMG>
          </div>
          <BenefitInfo>
            <h2>Tranquilidad y rentabilidad</h2>
            <p>
              Tener un <b>asesor financiero cerca de ti</b> velando por tus intereses te dará mayor control sobre tu
              patrimonio. Como tu <b>asesor financiero</b>, pondrá a trabajar tu dinero cuanto antes,
              sacándole el máximo partido y aprovechando las mejores oportunidades de los mercados.
            </p>
          </BenefitInfo>
        </Benefit>
        <Benefit>
          <div>
            <BenefitIMG>
              <img src={tiempoIMG} alt="Más tiempo para ti" width="1000" height="667" loading="lazy" />
            </BenefitIMG>
          </div>
          <BenefitInfo>
            <h2>Más tiempo para ti</h2>
            <p>
              Una buena <b>gestión financiera personal</b> exige tiempo y conocimientos. Si prefieres delegarla,
              un <b>asesor financiero para invertir</b> se ocupa de tu patrimonio mientras tú disfrutas de la vida.
              Puedes contar con él de forma <b>online</b> o presencialmente.
            </p>
          </BenefitInfo>
        </Benefit>

        <Section>
          <NewSectionInner>
            <SectionTitle
              title="Te ayudamos a encontrar al mejor asesor financiero"
              subtitle={<>Estas son algunas de las empresas de <b>asesoramiento financiero</b> que podrían ayudarte a conseguir tus objetivos económicos e inversiones.</>}
              noMargin
            />
          </NewSectionInner>

          <ProfessionalHelp>
            <HowItWorks>
              <div>
                <h3>¿Cómo funciona?</h3>
                <ol>
                  <li>
                    <Step>Ponte en contacto con nosotros completando nuestro pequeño formulario.</Step>
                  </li>
                  <li>
                    <Step>
                      En función de tus respuestas, te asignaremos hasta 3 asesores financieros profesionales. Los que
                      mejor se adapten a tus necesidades.
                    </Step>
                  </li>
                  <li>
                    <Step>
                      Los asesores se pondrán en contacto contigo para presentarte sus propuestas. ¡La decisión está en
                      tus manos!
                    </Step>
                  </li>
                  <li>
                    <Step>
                      Te enviaremos una breve encuesta de satisfacción y usaremos tus comentarios para seguir mejorando
                      nuestro servicio.
                    </Step>
                  </li>
                </ol>
                <br />
                <p>
                  No dudes en consultarnos cualquier duda que te pueda surgir{' '}
                  <a
                    href="https://www.finect.com/mensajes/nuevo?members=8A2720AF53FF11EAB67802000AEE01C9"
                    target="_blank"
                    rel="noreferrer nofollow"
                  >
                    aquí.
                  </a>{' '}
                  ¡A por ello!
                </p>
                <ArticleCTA toggleModal={toggleModal} />
                <Small display="block" textAlign="center" margin="30px 0 0" light>
                  Finect no presta servicios de asesoramiento financiero, simplemente te pone en contacto con el asesor
                  que tú elijas. Para más información consulta las{' '}
                  <AppLink
                    rel="nofollow"
                    data-fi-event="home-asesores"
                    href={`https://${process.env.FINECT_BASEURL || 'www.finect.com'}/terminos-legales`}
                  >
                    condiciones generales de uso
                  </AppLink>
                  .
                </Small>
              </div>
            </HowItWorks>
          </ProfessionalHelp>
        </Section>

        <Section>
          <NewSectionInner>
            <SectionTitle
              title="Preguntas frecuentes sobre asesores financieros"
              subtitle={<>Resolvemos las dudas más habituales sobre el <b>asesoramiento financiero</b>.</>}
            />
            <FaqItem title="¿Cuánto cobra un asesor financiero en España?" path="/asesores/saber/cuanto-cuesta">
              <p>
                ¿Crees que los <b>asesores financieros</b> son solo para grandes patrimonios? No es
                así. Tanto si buscas un <b>asesor financiero</b> como un experto financiero vinculado a
                una entidad, existen opciones más accesibles de lo que imaginas, sea cual sea tu nivel de ahorro. Aquí
                revisamos cuánto cobra un asesor financiero en España.
              </p>
            </FaqItem>
            <FaqItem title="¿Necesitas tener un asesor financiero?" path="/asesores/saber/necesitas-un-asesor">
              <p>
                Contar con un <b>asesor financiero</b> que conozca tus metas y se preocupe por tus intereses puede
                marcar una gran diferencia en la gestión de tu dinero. Un buen <b>asesor financiero</b> no solo te ayuda
                a preservar tu patrimonio, sino también a hacerlo crecer con criterio y de forma adaptada a tu perfil de
                riesgo.
              </p>
            </FaqItem>
            {/* <FaqItem
              title="¿Con qué objetivos puede ayudarte un asesor?"
              linkText="¿Con qué objetivos puede ayudarte?"
              path="/asesores/ayuda/como-ayudar"
            >
              <p>
                Un asesor financiero puede ayudarte a: invertir tus ahorros, preparar tu jubilación, pagar menos
                impuestos, decidir qué hacer con una herencia o ingresos extra, ahorrar para los estudios.
              </p>
            </FaqItem> */}
            <FaqItem title="¿Cómo tiene que ser tu asesor financiero?" path="/asesores/saber/como-tiene-que-ser">
              <p>
                Tu <b>asesor financiero</b> debe ser un profesional cuyo único interés sea tu bienestar financiero.
                Tiene que conocer a fondo tus metas y, en base a esa información, ayudarte a organizar tus ahorros y a
                tomar las mejores decisiones para <b>invertir</b> de forma alineada con tus objetivos y tu tolerancia
                al riesgo.
              </p>
            </FaqItem>
            <FaqItem title="¿De qué hablar con nuestro asesor financiero?" path="/asesores/saber/de-que-hablar">
              <p>
                En las primeras conversaciones con tu <b>asesor financiero</b>, lo importante no son las
                rentabilidades sino tus proyectos de vida y objetivos económicos. Ya sea de forma <b>online</b> o
                presencial, un buen asesor escuchará primero tu situación personal antes de hablar de productos o
                carteras. Eso llegará después.
              </p>
            </FaqItem>
          </NewSectionInner>
        </Section>

        <Container>
          <ContainerInner>
            <MainContent>
              <Provinces staticContext={staticContext} provinces={provinces} />
            </MainContent>
          </ContainerInner>
        </Container>

        <BottomCTA toggleModal={() => toggleModal()} />

        <Section>
          <TopQuestions staticContext={staticContext} />
        </Section>

        <Container>
          <ContainerInner>
            <MainContent noMargin>
              <Disclaimer />
            </MainContent>
          </ContainerInner>
        </Container>
      </LandingContent>
    );
  }
}
